import React, { useState } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import { z } from 'zod';
import { toFormikValidationSchema } from 'zod-formik-adapter';
import { Button, Label, TextInput, ToggleSwitch } from 'flowbite-react';
import * as wasm from 'sched';
import { v4 as uuidv4 } from 'uuid';
import { FiPlus, FiX } from 'react-icons/fi';

const NativeDecisionSchema = z.object({
  title: z.string().min(1, 'Title is required'),
  exclusive: z.boolean().default(false),
});

type NativeDecisionFormValues = z.infer<typeof NativeDecisionSchema>;

interface NativeDecisionFormProps {
  onSubmit: (command: wasm.DatabaseCommand) => Promise<void>;
  onClose: () => void;
  isOpen: boolean;
}

const NativeDecisionForm: React.FC<NativeDecisionFormProps> = ({ onSubmit, onClose, isOpen }) => {
  const [choices, setChoices] = useState<string[]>([]);
  const [newChoice, setNewChoice] = useState('');

  if (!isOpen) return null;

  const handleAddChoice = () => {
    const trimmed = newChoice.trim();
    if (trimmed && !choices.includes(trimmed)) {
      setChoices([...choices, trimmed]);
      setNewChoice('');
    }
  };

  const handleRemoveChoice = (index: number) => {
    setChoices(choices.filter((_, i) => i !== index));
  };

  const handleSubmit = async (values: NativeDecisionFormValues) => {
    const id = uuidv4();
    const command: wasm.DatabaseCommand = {
      type: 'CreateItem',
      id,
      item: {
        type: 'Decision',
        value: {
          title: values.title,
          choices,
          exclusive: values.exclusive,
          updated_at: new Date().toISOString(),
        },
      },
    };
    await onSubmit(command);
    setChoices([]);
    setNewChoice('');
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-cm-bg-surface p-6 rounded-lg shadow-lg w-full max-w-md">
        <h2 className="text-xl font-semibold mb-4 text-cm-text-primary">Create Decision Node</h2>

        <Formik
          initialValues={{ title: '', exclusive: false }}
          validationSchema={toFormikValidationSchema(NativeDecisionSchema)}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting, values, setFieldValue }) => (
            <Form className="space-y-4">
              <div>
                <div className="mb-2">
                  <Label htmlFor="title">Title</Label>
                </div>
                <Field
                  as={TextInput}
                  id="title"
                  name="title"
                  autoFocus
                  placeholder="Enter decision title"
                />
                <ErrorMessage name="title" component="div" className="text-red-500 text-sm mt-1" />
              </div>

              <div>
                <Label className="mb-2 block">Choices</Label>
                <div className="space-y-1 mb-2">
                  {choices.map((choice, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <span className="flex-1 text-sm text-cm-text-primary bg-cm-bg-elevated px-2 py-1 rounded">
                        {choice}
                      </span>
                      <button
                        type="button"
                        onClick={() => handleRemoveChoice(i)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <FiX className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <TextInput
                    value={newChoice}
                    onChange={e => setNewChoice(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddChoice(); } }}
                    placeholder="Add a choice..."
                    sizing="sm"
                    className="flex-1"
                  />
                  <Button type="button" size="sm" color="gray" onClick={handleAddChoice}>
                    <FiPlus />
                  </Button>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <ToggleSwitch
                  checked={values.exclusive}
                  onChange={val => setFieldValue('exclusive', val)}
                  label="Exclusive (only one choice may be selected)"
                />
              </div>

              <div className="flex justify-end space-x-2 mt-6">
                <Button color="gray" onClick={onClose}>Cancel</Button>
                <Button type="submit" disabled={isSubmitting}>Create Decision</Button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default NativeDecisionForm;
